import React from "react";
import { useLocation } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { useProjects } from "../../context/ProjectsContext";
import {
  DASHBOARD_TUTORIAL_EVENT,
  loadDashboardTutorialState,
  tutorialByKey,
} from "../../utils/dashboardTutorial";

// WebProfit Guided Onboarding · telemetry bridge
// STATUS: BLOCK_1_STABLE_CANDIDATE · passive instrumentation only.
// SCOPE: tutorial lifecycle, active/idle/hidden screen time and structural form friction.
// PRIVACY GUARANTEE: this bridge NEVER sends form values, free text, passwords, tokens or uploaded filenames.
// TEST STATE: syntax checked; runtime verification required in staging with Network tab + SuperPanel analytics.

const IDLE_AFTER_MS = 20_000;
const TICK_MS = 1_000;
const PROGRESS_THROTTLE_MS = 8_000;
const MAX_FORM_FIELDS = 120;

function projectKey(project) {
  return String(project?.slug || project?.id || project?._id || "").trim();
}

function projectIdentity(project) {
  return {
    projectId: String(project?.id || project?._id || ""),
    projectSlug: String(project?.slug || ""),
    projectName: String(project?.name || ""),
  };
}

function clean(value, max = 180) {
  return String(value || "").trim().slice(0, max);
}

function sessionId(session) {
  return clean(session?.sessionId || `${session?.tutorialKey || "tutorial"}:${session?.startedAt || "unknown"}`, 180);
}

function telemetryMetadata(session, extra = {}) {
  return {
    analyticsOnly: true,
    telemetryVersion: 1,
    tutorialVersion: Number(session?.version || 0),
    tutorialSessionId: sessionId(session),
    tutorialKey: clean(session?.tutorialKey, 80),
    tutorialSource: clean(session?.source, 100),
    stageKey: clean(session?.stageKey, 100),
    stepIndex: Math.max(0, Number(session?.step || 0)),
    ...extra,
  };
}

function send(project, session, action, path, metadata = {}, severity = "") {
  if (!project || !sessionId(session)) return Promise.resolve(null);
  return activityApi.trackPanel({
    action,
    ...projectIdentity(project),
    path,
    journey: {
      channel: "panel",
      sessionId: sessionId(session),
      path,
      step: Math.max(0, Number(session?.step || 0)),
    },
    metadata: telemetryMetadata(session, {
      path,
      ...(severity ? { clientSeverity: severity } : {}),
      ...metadata,
    }),
  }).catch(() => null);
}

function isTrackableControl(element) {
  if (!element || !(element instanceof HTMLElement)) return false;
  if (!element.matches("input, select, textarea")) return false;
  const type = clean(element.getAttribute("type"), 40).toLowerCase();
  return !["hidden", "submit", "button", "reset", "image"].includes(type);
}

function controlKey(element, index = 0) {
  // Only structural identifiers are reported. Never the value.
  return clean(
    element?.getAttribute("data-wp-field-key")
      || element?.getAttribute("name")
      || element?.id
      || element?.getAttribute("aria-label")
      || `field-${index + 1}`,
    120,
  );
}

function controlType(element) {
  return clean(element?.getAttribute("type") || element?.tagName || "field", 40).toLowerCase();
}

function isFilled(element) {
  if (!isTrackableControl(element)) return false;
  const type = controlType(element);
  if (type === "checkbox" || type === "radio") return Boolean(element.checked);
  if (type === "file") return Number(element.files?.length || 0) > 0;
  return String(element.value || "").trim().length > 0;
}

function formControls(form) {
  return Array.from(form?.querySelectorAll?.("input, select, textarea") || [])
    .filter(isTrackableControl)
    .slice(0, MAX_FORM_FIELDS);
}

function formSnapshot(form) {
  const controls = formControls(form);
  const required = controls.filter((element) => element.required || element.getAttribute("aria-required") === "true");
  const completed = controls.filter(isFilled);
  const completedRequired = required.filter(isFilled);
  const invalid = controls.filter((element) => element.matches?.(":invalid"));
  return {
    totalFields: controls.length,
    completedFields: completed.length,
    requiredFields: required.length,
    completedRequiredFields: completedRequired.length,
    invalidFields: invalid.length,
    completionPct: controls.length ? Math.round((completed.length / controls.length) * 1000) / 10 : 0,
    requiredCompletionPct: required.length ? Math.round((completedRequired.length / required.length) * 1000) / 10 : 100,
  };
}

function resolveForm(target) {
  if (!target || !(target instanceof HTMLElement)) return null;
  return target.closest("form,[data-wp-telemetry-form]");
}

function formKey(form) {
  if (!form) return "";
  const explicit = form.getAttribute("data-wp-telemetry-form")
    || form.getAttribute("data-wp-form-key")
    || form.id
    || form.getAttribute("name")
    || form.getAttribute("aria-label");
  if (explicit) return clean(explicit, 140);
  const forms = Array.from(document.querySelectorAll("form,[data-wp-telemetry-form]"));
  const index = Math.max(0, forms.indexOf(form));
  return `form-${index + 1}`;
}

function createScreenState(path) {
  const now = Date.now();
  return {
    path,
    startedAt: now,
    lastTickAt: now,
    lastInteractionAt: now,
    firstInteractionAt: 0,
    activeMs: 0,
    idleMs: 0,
    hiddenMs: 0,
    interactions: 0,
    maxContinuousIdleMs: 0,
    forms: new Map(),
  };
}

function tickScreen(state) {
  if (!state) return;
  const now = Date.now();
  const delta = Math.max(0, Math.min(5_000, now - state.lastTickAt));
  state.lastTickAt = now;
  if (document.visibilityState !== "visible") {
    state.hiddenMs += delta;
    return;
  }
  const currentIdleMs = Math.max(0, now - state.lastInteractionAt);
  if (currentIdleMs >= IDLE_AFTER_MS) {
    state.idleMs += delta;
    state.maxContinuousIdleMs = Math.max(state.maxContinuousIdleMs, currentIdleMs);
  } else {
    state.activeMs += delta;
  }
}

function getOrCreateFormState(screen, form) {
  if (!screen || !form) return null;
  const key = formKey(form);
  if (!screen.forms.has(key)) {
    screen.forms.set(key, {
      key,
      startedAt: Date.now(),
      lastProgressAt: 0,
      lastCompletionPct: -1,
      changes: 0,
      focuses: 0,
      blurs: 0,
      validationErrors: 0,
      submitAttempts: 0,
      submitted: false,
      firstInteractionDelayMs: Math.max(0, Date.now() - screen.startedAt),
      lastSnapshot: null,
    });
  }
  return screen.forms.get(key);
}

function formPayload(form, state) {
  const snapshot = form ? formSnapshot(form) : (state?.lastSnapshot || {});
  if (state && form) state.lastSnapshot = snapshot;
  return {
    formKey: state?.key || formKey(form),
    ...snapshot,
    changes: Number(state?.changes || 0),
    focuses: Number(state?.focuses || 0),
    blurs: Number(state?.blurs || 0),
    validationErrors: Number(state?.validationErrors || 0),
    submitAttempts: Number(state?.submitAttempts || 0),
    firstInteractionDelayMs: Number(state?.firstInteractionDelayMs || 0),
    formActiveMs: state?.startedAt ? Math.max(0, Date.now() - state.startedAt) : 0,
  };
}

export default function OnboardingTelemetryBridge() {
  const location = useLocation();
  const { activeProject } = useProjects();
  const path = `${location.pathname}${location.search || ""}`;
  const activeProjectKey = projectKey(activeProject);
  const sessionRef = React.useRef(loadDashboardTutorialState(activeProject));
  const projectRef = React.useRef(activeProject);
  const pathRef = React.useRef(path);
  const screenRef = React.useRef(null);

  React.useEffect(() => {
    projectRef.current = activeProject;
    const stored = loadDashboardTutorialState(activeProject);
    sessionRef.current = stored;
  }, [activeProject, activeProjectKey]);

  const flushScreen = React.useCallback((reason = "route_change") => {
    const screen = screenRef.current;
    const session = sessionRef.current;
    const project = projectRef.current;
    if (!screen || !session?.active || !project) {
      screenRef.current = null;
      return;
    }
    tickScreen(screen);

    const totalMs = Math.max(0, Date.now() - screen.startedAt);
    const accountedMs = screen.activeMs + screen.idleMs + screen.hiddenMs;
    send(project, session, "onboarding_screen_summary", screen.path, {
      screenKey: clean(screen.path.split("?")[0], 300),
      closeReason: clean(reason, 80),
      totalMs,
      activeMs: Math.round(screen.activeMs),
      idleMs: Math.round(screen.idleMs),
      hiddenMs: Math.round(screen.hiddenMs),
      unclassifiedMs: Math.max(0, totalMs - accountedMs),
      interactionCount: screen.interactions,
      firstInteractionDelayMs: screen.firstInteractionAt
        ? Math.max(0, screen.firstInteractionAt - screen.startedAt)
        : totalMs,
      maxContinuousIdleMs: Math.round(screen.maxContinuousIdleMs),
      idleRate: totalMs ? Math.round((screen.idleMs / totalMs) * 1000) / 10 : 0,
      hiddenRate: totalMs ? Math.round((screen.hiddenMs / totalMs) * 1000) / 10 : 0,
    });

    screen.forms.forEach((formState) => {
      const escapedKey = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(formState.key) : formState.key;
      const selector = `[data-wp-telemetry-form="${escapedKey}"]`;
      let form = null;
      try {
        form = document.getElementById(formState.key)
          || document.querySelector(`form[name="${formState.key.replaceAll('"', '\\"')}"]`)
          || document.querySelector(selector);
      } catch (_) {
        form = null;
      }
      const structural = formPayload(form, formState);
      const fallbackStructural = Object.keys(structural).length ? structural : {
        formKey: formState.key,
        changes: formState.changes,
        focuses: formState.focuses,
        blurs: formState.blurs,
        validationErrors: formState.validationErrors,
        submitAttempts: formState.submitAttempts,
        firstInteractionDelayMs: formState.firstInteractionDelayMs,
        formActiveMs: Math.max(0, Date.now() - formState.startedAt),
      };
      const requiredIncomplete = Number(structural.requiredFields || 0) > 0
        ? Number(structural.requiredCompletionPct || 0) < 100
        : Number(structural.completionPct || 0) < 100;
      const abandoned = !formState.submitted && formState.changes > 0 && requiredIncomplete;
      send(project, session, abandoned ? "onboarding_form_abandoned" : "onboarding_form_summary", screen.path, {
        ...(Object.keys(structural).length ? structural : fallbackStructural),
        closeReason: clean(reason, 80),
        submitted: Boolean(formState.submitted),
      });
    });
    screenRef.current = null;
  }, []);

  React.useEffect(() => {
    const previousPath = pathRef.current;
    if (previousPath !== path) flushScreen("route_change");
    pathRef.current = path;
    const session = sessionRef.current;
    if (session?.active && activeProject) {
      screenRef.current = createScreenState(path);
      send(activeProject, session, "onboarding_step_viewed", path, {
        viewReason: previousPath === path ? "mount" : "route_change",
        tutorialLabel: tutorialByKey(session.tutorialKey)?.shortLabel || session.tutorialKey,
      });
    }
  }, [path, activeProjectKey, activeProject, flushScreen]);

  React.useEffect(() => {
    const timer = window.setInterval(() => tickScreen(screenRef.current), TICK_MS);

    const onInteraction = () => {
      const screen = screenRef.current;
      if (!screen || !sessionRef.current?.active) return;
      const now = Date.now();
      screen.interactions += 1;
      screen.lastInteractionAt = now;
      if (!screen.firstInteractionAt) screen.firstInteractionAt = now;
    };

    const onFocusIn = (event) => {
      onInteraction();
      const form = resolveForm(event.target);
      if (!form || !screenRef.current || !sessionRef.current?.active) return;
      const state = getOrCreateFormState(screenRef.current, form);
      state.focuses += 1;
      if (state.focuses === 1 && state.changes === 0) {
        send(projectRef.current, sessionRef.current, "onboarding_form_started", pathRef.current, formPayload(form, state));
      }
    };

    const onFocusOut = (event) => {
      const form = resolveForm(event.target);
      if (!form || !screenRef.current || !sessionRef.current?.active) return;
      const state = getOrCreateFormState(screenRef.current, form);
      state.blurs += 1;
    };

    const onChange = (event) => {
      onInteraction();
      const form = resolveForm(event.target);
      if (!form || !screenRef.current || !sessionRef.current?.active) return;
      const state = getOrCreateFormState(screenRef.current, form);
      state.changes += 1;
      const snapshot = formSnapshot(form);
      const now = Date.now();
      const bucketChanged = Math.floor(snapshot.completionPct / 25) !== Math.floor(Math.max(0, state.lastCompletionPct) / 25);
      if (now - state.lastProgressAt >= PROGRESS_THROTTLE_MS || bucketChanged) {
        state.lastProgressAt = now;
        state.lastCompletionPct = snapshot.completionPct;
        send(projectRef.current, sessionRef.current, "onboarding_form_progress", pathRef.current, formPayload(form, state));
      }
    };

    const onInvalid = (event) => {
      const form = resolveForm(event.target);
      if (!form || !screenRef.current || !sessionRef.current?.active) return;
      const state = getOrCreateFormState(screenRef.current, form);
      state.validationErrors += 1;
      const controls = formControls(form);
      const index = Math.max(0, controls.indexOf(event.target));
      send(projectRef.current, sessionRef.current, "onboarding_form_validation_error", pathRef.current, {
        ...formPayload(form, state),
        fieldKey: controlKey(event.target, index),
        fieldType: controlType(event.target),
        fieldRequired: Boolean(event.target?.required || event.target?.getAttribute?.("aria-required") === "true"),
      }, "warning");
    };

    const onSubmit = (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : resolveForm(event.target);
      if (!form || !screenRef.current || !sessionRef.current?.active) return;
      const state = getOrCreateFormState(screenRef.current, form);
      state.submitAttempts += 1;
      state.submitted = true;
      send(projectRef.current, sessionRef.current, "onboarding_form_submit_attempt", pathRef.current, formPayload(form, state));
    };

    const onPageHide = () => flushScreen("page_hide");
    const passive = { passive: true };
    window.addEventListener("pointerdown", onInteraction, passive);
    window.addEventListener("keydown", onInteraction);
    window.addEventListener("scroll", onInteraction, passive);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("input", onChange, true);
    document.addEventListener("change", onChange, true);
    document.addEventListener("invalid", onInvalid, true);
    document.addEventListener("submit", onSubmit, true);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
      window.removeEventListener("scroll", onInteraction);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("input", onChange, true);
      document.removeEventListener("change", onChange, true);
      document.removeEventListener("invalid", onInvalid, true);
      document.removeEventListener("submit", onSubmit, true);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [flushScreen]);

  React.useEffect(() => {
    const onTutorialEvent = (event) => {
      const type = clean(event?.detail?.type, 40);
      const incoming = event?.detail?.session || sessionRef.current;
      const project = projectRef.current;
      if (!project || !incoming) return;
      if (type === "metric" && !incoming?.active) return;
      const currentProjectKey = projectKey(project);
      if (incoming.projectKey && currentProjectKey && incoming.projectKey !== currentProjectKey) return;

      if (["stop", "complete", "restart", "pause"].includes(type)) flushScreen(`tutorial_${type}`);
      sessionRef.current = incoming;

      const actionByType = {
        launcher_open: "onboarding_launcher_opened",
        start: "onboarding_tutorial_started",
        restart: "onboarding_tutorial_restarted",
        stop: "onboarding_tutorial_exited",
        complete: "onboarding_tutorial_completed",
        pause: "onboarding_tutorial_paused",
        resume: "onboarding_tutorial_resumed",
        step: "onboarding_step_viewed",
        block_started: "onboarding_block_started",
        block_completed: "onboarding_block_completed",
        block_skipped: "onboarding_block_skipped",
        block_deferred: "onboarding_block_deferred",
        target_engaged: "onboarding_target_engaged",
        blocked: "onboarding_step_blocked",
      };
      const metricActionByType = {
        import_started: "onboarding_import_started",
        import_completed: "onboarding_import_completed",
        import_failed: "onboarding_import_failed",
        import_abandoned: "onboarding_import_abandoned",
        stock_count_started: "onboarding_stock_count_started",
        stock_count_progress: "onboarding_stock_count_progress",
        stock_count_completed: "onboarding_stock_count_completed",
        stock_count_failed: "onboarding_stock_count_failed",
        stock_count_paused: "onboarding_stock_count_paused",
        stock_count_abandoned: "onboarding_stock_count_abandoned",
        item_entry_started: "onboarding_item_entry_started",
        item_entry_completed: "onboarding_item_entry_completed",
        item_entry_failed: "onboarding_item_entry_failed",
        item_entry_abandoned: "onboarding_item_entry_abandoned",
        item_validation_blocked: "onboarding_item_validation_blocked",
        item_form_step_viewed: "onboarding_item_form_step_viewed",
        item_form_step_completed: "onboarding_item_form_step_completed",
        item_form_step_skipped: "onboarding_item_form_step_skipped",
        first_item_handoff_started: "onboarding_first_item_handoff_started",
        cash_register_completed: "onboarding_cash_register_completed",
        access_member_completed: "onboarding_access_member_completed",
        movement_sale_completed: "onboarding_sale_completed",
        movement_purchase_completed: "onboarding_purchase_completed",
        fiscal_connection_completed: "onboarding_fiscal_connection_completed",
        payment_connection_completed: "onboarding_payment_connection_completed",
        step_completed: "onboarding_step_completed",
        owner_item_deleted: "onboarding_owner_item_deleted",
        owner_movement_removed: "onboarding_owner_movement_removed",
      };
      const action = type === "metric"
        ? metricActionByType[clean(event?.detail?.metricType, 80)]
        : actionByType[type];
      if (action) {
        send(project, incoming, action, pathRef.current, {
          eventReason: clean(event?.detail?.reason || incoming?.stopReason || incoming?.completionReason, 120),
          previousSessionId: clean(event?.detail?.previousSessionId, 180),
          blockReason: clean(event?.detail?.reason, 120),
          metricType: clean(event?.detail?.metricType, 80),
          itemCount: Math.max(0, Number(event?.detail?.itemCount || 0)),
          processedCount: Math.max(0, Number(event?.detail?.processedCount || 0)),
          errorCount: Math.max(0, Number(event?.detail?.errorCount || 0)),
          sourceType: clean(event?.detail?.sourceType, 80),
          operationId: clean(event?.detail?.operationId, 180),
          reasonCode: clean(event?.detail?.reasonCode, 100),
          completionPct: Math.max(0, Math.min(100, Number(event?.detail?.completionPct || 0))),
          attemptNumber: Math.max(0, Number(event?.detail?.attemptNumber || 0)),
          blockKey: clean(event?.detail?.blockKey || incoming?.blockKey || incoming?.stageKey, 100),
          result: clean(event?.detail?.result, 40),
          dwellMs: Math.max(0, Number(event?.detail?.dwellMs || 0)),
          targetWaitMs: Math.max(0, Number(event?.detail?.targetWaitMs || 0)),
          planLength: Math.max(0, Number(event?.detail?.planLength || incoming?.planKeys?.length || 0)),
          movementKind: clean(event?.detail?.movementKind, 60),
          registerType: clean(event?.detail?.registerType, 60),
          memberRole: clean(event?.detail?.memberRole, 60),
          connectionKind: clean(event?.detail?.connectionKind, 60),
          formStepIndex: Math.max(0, Number(event?.detail?.stepIndex || 0)),
          formStepKey: clean(event?.detail?.stepKey, 120),
          formStepOptional: event?.detail?.optional === true,
          activeStepMs: Math.max(0, Number(event?.detail?.activeStepMs || 0)),
          previousStatus: clean(event?.detail?.previousStatus, 40),
          preservedHistory: event?.detail?.preservedHistory === true,
        });
      }

      if (["start", "restart", "resume", "step", "block_started"].includes(type) && incoming.active) {
        screenRef.current = createScreenState(pathRef.current);
      }
    };

    window.addEventListener(DASHBOARD_TUTORIAL_EVENT, onTutorialEvent);
    return () => window.removeEventListener(DASHBOARD_TUTORIAL_EVENT, onTutorialEvent);
  }, [flushScreen]);

  React.useEffect(() => () => flushScreen("bridge_unmount"), [flushScreen]);
  return null;
}
